import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import theme from "../UI/theme";
import { fetchTableStructure, getAvailableSqlQueries, saveCustomSqlQuery, testCustomSqlQuery } from "../scripts/customSql";
import CustomSqlTable from "../components/admin/customSqlTable/CustomSqlTable";
import Button from "../components/UiComponents/Button";
import CustomSqlTableModal from "../components/admin/customSqlTable/CustomSqlTableModal";
import { ModalText } from "../components/panel/ModalStyles";
import InputField from "../components/UiComponents/InputField";

const ViewContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px;
`

const ViewHeaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    border-bottom: 2px solid ${theme.colors.darkSpace80};

`
const GlobalFilterContainer = styled.div`
    display: flex;
    gap: 10px;
    border-bottom: 1px solid ${theme.colors.darkSpace80};

`

const TablesContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-evenly;
    flex-wrap: wrap;
    gap: 20px;
`

const ButtonRowContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    gap: 12px;
    margin-bottom: 12px;
`

const PillContainer = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin: 12px 0;
`

const Pill = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;
    background-color: ${theme.colors.darkSpace90};
    color: ${theme.colors.darkSpace10};
    border-radius: 18px;
    font-size: ${theme.fontSize.small};
    border: 1px solid ${theme.colors.greenEnergy};
    margin: 1px 2px;

    &:hover {
        cursor: pointer;
        color: ${theme.colors.greenEnergy};
        ${({ $selected }) =>
            $selected &&
            css`
                color: black;
            `
        }

    }

    ${({ $selected}) =>
        $selected &&
        css`
            background-color: ${theme.colors.greenEnergy};
            color: black;
            border-color: ${theme.colors.greenEnergy};
        `
    }

    ${({ isLog }) =>
        isLog &&
        css`
            border-color: ${theme.colors.raspberryRed};
            &:hover {
                color: ${theme.colors.raspberryRed};
            }
        `
    }

    ${({ isLog, $selected }) =>
        isLog && $selected &&
        css`
            background-color: ${theme.colors.raspberryRed};
            color: black;
            border-color: ${theme.colors.raspberryRed};

            &:hover {
                color: black;
            }
        `
    }

`;

const ColumnPill = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 6px;
    background-color: ${theme.colors.darkSpace80};
    color: ${theme.colors.darkSpace10};
    font-size: ${theme.fontSize.superTiny};
    border: 1px solid ${theme.colors.textGray};
    margin: 4px 2px;
`;

const TABLE_CATEGORY_RULES = [
    {
        key: "log",
        label: "Logs",
        matchers: [
            "log",
            { type: "equals", value: "userinteractions" },
            { type: "equals", value: "trends" },
            { type: "equals", value: "sensortrends" },
        ],
        pillProps: { isLog: true },
        skipPreviewFetch: true,
        sortPriority: 1,
    },
];

const DEFAULT_TABLE_CATEGORY = {
    key: "table",
    label: "Tables",
    matchers: [],
    pillProps: {},
    skipPreviewFetch: false,
    sortPriority: 0,
};

const MATCHER_STRATEGIES = {
    includes: (name, value) => name.includes(value),
    equals: (name, value) => name === value,
    startsWith: (name, value) => name.startsWith(value),
    endsWith: (name, value) => name.endsWith(value),
};

const normalizeName = (rawName = "") => (rawName || "").toString().toLowerCase();

const doesMatcherApply = (name, matcher) => {
    if (typeof matcher === "string") {
        const normalizedString = matcher.toLowerCase();
        if (!normalizedString) return false;
        return MATCHER_STRATEGIES.includes(name, normalizedString);
    }

    const { type = "includes", value = "" } = matcher || {};
    const normalizedValue = value === undefined || value === null ? "" : value.toString().toLowerCase();
    if (!normalizedValue) return false;
    const strategy = MATCHER_STRATEGIES[type] || MATCHER_STRATEGIES.includes;
    return strategy(name, normalizedValue);
};

const getCategoryMeta = (tableName) => {
    const normalized = normalizeName(tableName);
    if (!normalized) return DEFAULT_TABLE_CATEGORY;

    const matchedCategory = TABLE_CATEGORY_RULES.find(({ matchers }) =>
        Array.isArray(matchers) && matchers.some((matcher) => doesMatcherApply(normalized, matcher))
    );

    return matchedCategory || DEFAULT_TABLE_CATEGORY;
};

const isLogTable = (tableName) => getCategoryMeta(tableName).key === "log";
const getPillPropsForTable = (tableName) => getCategoryMeta(tableName).pillProps;
const shouldSkipPreviewFetch = (tableName) => getCategoryMeta(tableName).skipPreviewFetch;
const getSortPriority = (tableName) => getCategoryMeta(tableName).sortPriority;
const deriveGroupsFromQueries = (queries = []) => {
    const uniqueGroups = Array.from(new Set(queries.map((query) => query.tableGroup).filter(Boolean)));
    return uniqueGroups.length ? ["Show All", ...uniqueGroups, "Other"] : [];
};

const CustomTablesView = ({}) => {
    const [databaseStructure, setDatabaseStructure] = useState([]);
    const [showTables, setShowTables] = useState(false);
    const [selectedPill, setSelectedPill] = useState(null);
    const [selectedTableData, setSelectedTableData] = useState(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [showLogs, setShowLogs] = useState(false);
    const [copiedSuccess, setCopiedSuccess] = useState(false);
    const [propSearch, setPropSearch] = useState("");
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState("Show All");
    const [availableQueries, setAvailableQueries] = useState([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshAt, setLastRefreshAt] = useState(null);
    const [dataRefreshKey, setDataRefreshKey] = useState(0);

    const fetchDatabaseStructure = useCallback(async () => {
        const data = await fetchTableStructure();
        const structure = Array.isArray(data?.tables) ? data.tables : [];
        // sort structure alphabetically by table name but keep any tables with 'log' in the name at the end
        const sortedStructure = structure.sort((a, b) => {
            const aPriority = getSortPriority(a?.name);
            const bPriority = getSortPriority(b?.name);
            if (aPriority !== bPriority) return aPriority - bPriority;

            const aName = a?.name || "";
            const bName = b?.name || "";
            return aName.localeCompare(bName);
        });
        setDatabaseStructure(sortedStructure);
    }, []);

    const fetchTableQueries = useCallback(async ({ withLoading = false } = {}) => {
        if (withLoading) {
            setIsRefreshing(true);
        }
        try {
            const data = await getAvailableSqlQueries();
            const queries = Array.isArray(data?.queries) ? data.queries : [];
            setAvailableQueries(queries);
            setGroups(deriveGroupsFromQueries(queries));
            setLastRefreshAt(new Date());
            setDataRefreshKey(Date.now());
        } catch (error) {
            console.error("Failed to load custom SQL queries", error);
            setAvailableQueries([]);
            setGroups([]);
        } finally {
            if (withLoading) {
                setIsRefreshing(false);
            }
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const stateFromUrl = params.get("group");

        if (stateFromUrl !== null) {
            setSelectedGroup(stateFromUrl);
        }
    }, []);

    useEffect(() => {
        fetchDatabaseStructure();
    }, [fetchDatabaseStructure]);

    useEffect(() => {
        fetchTableQueries();
    }, [fetchTableQueries]);

    useEffect(() => {
        if (groups.length === 0) {
            return;
        }

        if (!groups.includes(selectedGroup)) {
            setSelectedGroup("Show All");
        }
    }, [groups, selectedGroup]);

    const emptyInitialValues = useMemo(
        () => ({
            name: "",
            description: "",
            cgptPrompt: "",
            sqlQuery: "",
            columnOrder: [],
            tableNumber: "",
            tableGroup: "",
            notifyIf: [],
        }),
        []
    );

    const handleCreateSave = async ({
        name,
        description,
        cgptPrompt,
        sqlQuery,
        columnOrder,
        tableNumber,
        tableGroup,
        notifyIf,
    }) => {
        await saveCustomSqlQuery({
            name,
            description,
            cgptPrompt,
            sqlQuery,
            columnOrder,
            tableNumber,
            tableGroup,
            notifyIf,
        });
        await fetchTableQueries();
        setIsCreateModalOpen(false);
    };

    const handleCloseCreateModal = () => {
        setIsCreateModalOpen(false);
    };

    const selectPillHandler = async (table) => {
        setCopiedSuccess(false);
        setSelectedPill(table);
        if (shouldSkipPreviewFetch(table.name)) {
            const tableColumns = Array.isArray(table.columns) ? table.columns : [];
            setSelectedTableData({
                name: table.name,
                description:
                    <ModalText>
                        This is a very large table, so to avoid performance issues, fetching data has been omitted.<br />
                        <h4 style={{marginBottom: '0'}}>Its columns are:</h4> <br />
                            {tableColumns.map((col, idx) => (
                                <ColumnPill key={idx} >
                                    {col.columnName}
                                </ColumnPill>
                            ))}
                    </ModalText>,
                data: [],
            });
            return;
        }
        const testQuery = `SELECT * FROM ${table.name} LIMIT 500; `;
        const res = await testCustomSqlQuery(testQuery);
        const result = res?.result;

        if (!result || !Array.isArray(result.data)) {
            setSelectedTableData(null);
            return;
        }

        const previewTable = {
            ...result,
            name: table.name,
            description: `Structure of table ${table.name}, with ${result.data.length} sample rows.`,
        };

        setSelectedTableData(previewTable);
    }

    const copyToClipboard = (object) => {
        const structureString = JSON.stringify(object, null, 2);
        navigator.clipboard.writeText(structureString)
            .then(() => {
                setCopiedSuccess(true);
            }
            )
            .catch((err) => {
                alert("Error copying table structure to clipboard:");
                setCopiedSuccess(false);
            });
    };

    return (
        <ViewContainer>
            <ViewHeaderContainer>
                <h2>
                    Tables
                    <Button style={{marginLeft: '20px'}} tertiary small onClick={() => setShowTables(!showTables)}>{showTables ? "Hide" : "Show"} Tables</Button>
                    {showTables &&
                        <Button style={{float: 'right'}} tertiary small onClick={() => copyToClipboard(databaseStructure)}>Copy Database Structure</Button>
                    }
                </h2>
                {showTables && <>
                    <InputField
                        type="text"
                        placeholder="Search for properties..."
                        value={propSearch}
                        onChange={(e) => setPropSearch(e.target.value)}
                        style={{width: '300px'}}
                    />
                    <PillContainer>
                        {databaseStructure.map((table, index) => {
                            if (isLogTable(table.name)) return null;
                            const tableColumns = Array.isArray(table.columns) ? table.columns : [];
                            if (propSearch && !tableColumns.some(col => col.columnName.toLowerCase().includes(propSearch.toLowerCase()))) return null;
                            const pillProps = getPillPropsForTable(table.name);
                            return (
                                <Pill
                                    key={index}
                                    $selected={selectedPill?.name === table.name}
                                    onClick={() => selectPillHandler(table)}
                                    {...pillProps}
                                >
                                    {table.name}
                                </Pill>
                            )
                        })}
                    </PillContainer>

                    <h4 style={{margin: '4px 0 0 0'}}>Logs <Button style={{marginLeft: '20px'}} tertiary small onClick={() => setShowLogs(!showLogs)}>{showLogs ? "Hide" : "Show"}</Button></h4>
                    <PillContainer>
                        {databaseStructure.map((table, index) => {
                            if (!isLogTable(table.name)) return null;
                            if (!showLogs) return null;
                            const tableColumns = Array.isArray(table.columns) ? table.columns : [];
                            if (propSearch && !tableColumns.some(col => col.columnName.toLowerCase().includes(propSearch.toLowerCase()))) return null;
                            const pillProps = getPillPropsForTable(table.name);
                            return (
                                <Pill
                                    key={index}
                                    $selected={selectedPill?.name === table.name}
                                    onClick={() => selectPillHandler(table)}
                                    {...pillProps}
                                >
                                    {table.name}
                                </Pill>
                            )
                        })}
                    </PillContainer>
                    {(selectedPill && selectedTableData) && <>
                        <h2 >
                            {selectedPill.name}
                            <Button style={{marginLeft: '20px'}} tertiary small onClick={() => copyToClipboard(selectedPill)} >Copy Table Structure</Button>
                        </h2>
                        {copiedSuccess && <span style={{color: theme.colors.greenEnergy}}>Table structure copied to clipboard!</span>}
                        <CustomSqlTable
                            key={selectedPill.name}
                            query={selectedTableData}
                            showEditButton={false}
                            maxWidth="100%"
                            maxHeight="420px"
                            refreshKey={dataRefreshKey}
                        />
                        
                    </>}
                </>}
                
            </ViewHeaderContainer>

            <h3>Custom SQL queries</h3>
            <ButtonRowContainer>
                <Button primary onClick={() => setIsCreateModalOpen(true)}>Add New Query</Button>
                <Button
                    tertiary
                    onClick={() => fetchTableQueries({ withLoading: true })}
                    disabled={isRefreshing}
                >
                    {isRefreshing ? "Refreshing…" : "Refresh Data"}
                </Button>
                {lastRefreshAt && (
                    <span style={{ alignSelf: "center", color: theme.colors.textGray, fontSize: theme.fontSize.superTiny }}>
                        Last updated {lastRefreshAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                    </span>
                )}
            </ButtonRowContainer>

            {groups.length > 0 &&
                <PillContainer style={{borderTop: `2px solid ${theme.colors.darkSpace80}`, paddingTop: '12px'}}>
                    {groups.map((group, index) => (
                        <Pill
                            key={index}
                            $selected={selectedGroup === group}
                            onClick={() => setSelectedGroup(group)}
                        >
                            {group}
                        </Pill>
                    ))}
                </PillContainer>
            }
            
            <TablesContainer>
                {availableQueries.map((query, index) => {
                    let showTable = false;
                    if (selectedGroup === 'Show All') {
                        showTable = true;
                    } else if (selectedGroup === 'Other') {
                        if (!query.tableGroup) {
                            showTable = true;
                        }
                    } else if (query.tableGroup === selectedGroup) {
                        showTable = true;
                    }

                    if (!showTable) return null;
                    return (
                        <CustomSqlTable
                            key={query.queryId || index}
                            query={query}
                            databaseStructure={databaseStructure}
                            refreshKey={dataRefreshKey}
                        />
                    )
                })}
            </TablesContainer>

            <CustomSqlTableModal
                show={isCreateModalOpen}
                tableId="new"
                initialValues={emptyInitialValues}
                onClose={handleCloseCreateModal}
                onSave={handleCreateSave}
                onTest={testCustomSqlQuery}
                databaseStructure={databaseStructure}
            />
        </ViewContainer>
    );
}

export default CustomTablesView;